"use client"

import { useState, useEffect, useCallback } from "react"
import styled from "styled-components"
import { Heart, MessageSquare, Share, Bookmark, MoreHorizontal, ChevronLeft, ChevronRight, User, Edit, Trash2, X, ZoomIn } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import CommentSection from "../components/CommentSection"
import EditPostModal from "../components/EditPostModal"
import { useNavigate } from "react-router-dom"

const Card = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  margin-bottom: 20px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const UserName = styled.div`
  font-weight: 500;
  cursor: pointer;
  color: ${props => props.theme.textPrimary};
  
  &:hover {
    color: ${props => props.theme.buttonPrimary};
  }
`

const Timestamp = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const DropdownContainer = styled.div`
  position: relative;
`

const MoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    color: ${props => props.theme.textPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 120px;
  overflow: hidden;
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }

  &.danger {
    color: ${props => props.theme.error};
    
    &:hover {
      background-color: ${props => props.theme.hoverBackground};
    }
  }
`

const ImageContainer = styled.div`
  position: relative;
  
  .image-counter {
    position: absolute;
    top: 10px;
    left: 10px;
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
  }
  
  .navigation-dots {
    position: absolute;
    bottom: 16px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    gap: 8px;
  }
  
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    
    &.active {
      background-color: ${props => props.theme.buttonPrimary};
    }
  }
`

const PostImage = styled.img`
  width: 100%;
  max-height: 500px;
  object-fit: cover;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.92;
  }
`

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
`

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 4px;
  cursor: default;
`

const LightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255,255,255,0.15);
  border: none;
  color: white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10000;
  &:hover { background: rgba(255,255,255,0.3); }
`

const LightboxNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.15);
  border: none;
  color: white;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10000;
  &:hover { background: rgba(255,255,255,0.3); }
  &.prev { left: 20px; }
  &.next { right: 20px; }
`

const LightboxCounter = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 14px;
  background: rgba(0,0,0,0.5);
  padding: 4px 12px;
  border-radius: 12px;
  z-index: 10000;
`

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }
  
  &.prev {
    left: 16px;
  }
  
  &.next {
    right: 16px;
  }
`

const ImageCaption = styled.div`
  padding: 8px 16px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const CardActions = styled.div`
  display: flex;
  padding: 8px 16px;
  border-top: 1px solid ${props => props.theme.border};
`

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => (props.$active ? props.theme.buttonPrimary : props.theme.textSecondary)};
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.hoverBackground};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    fill: ${(props) => (props.$active ? props.theme.buttonPrimary : "none")};
    stroke: ${(props) => (props.$active ? props.theme.buttonPrimary : "currentColor")};
    transition: all 0.2s ease;
  }

  span {
    font-size: 12px;
    font-weight: 500;
  }
`

const CardFooter = styled.div`
  padding: 0 16px 16px;
`

const PostTags = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.buttonPrimary};
`

const PostDescription = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  white-space: pre-line;
  ${props => !props.$expanded && `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`

const ReadMoreButton = styled.span`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  opacity: 0.7;
  &:hover { opacity: 1; }
`

// 날짜를 포맷하는 함수
const formatDate = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  // 현재 시간과의 차이 계산
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  // 상대적 시간 표시
  if (diffSec < 60) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;
  
  // 날짜 표시
  return `${date.getFullYear()}.${date.getMonth()+1}.${date.getDate()}`;
};

export default function PostCard({ post, onPostUpdated, onPostDeleted }) {
  const { isLoggedIn, user } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [liked, setLiked] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [commentCount, setCommentCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [descExpanded, setDescExpanded] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [brokenImages, setBrokenImages] = useState(new Set())

  // 샘플 포스트 데이터
  const defaultPost = {
    author: "dexterity",
    authorAvatar: null,
    timestamp: new Date().toISOString(),
    images: ["/placeholder.svg?height=400&width=600"],
    imageCaption: "이미지 캡션",
    tags: ["샘플"],
    description: "샘플 게시물입니다.",
  }

  const postData = post || defaultPost
  
  // 이미지 배열 확인 및 URL 추출
  let imageUrls = [];
  if (Array.isArray(postData.images)) {
    // images가 객체 배열인 경우 (API 응답)
    if (postData.images.length > 0 && typeof postData.images[0] === 'object') {
      imageUrls = postData.images.map(img => img.url || img.image_url || img);
    } else {
      // images가 문자열 배열인 경우
      imageUrls = postData.images;
    }
  }
  // 중복 이미지 제거 + 깨진 이미지 필터링
  const images = imageUrls
    .map(u => (typeof u === 'string' ? u : ''))
    .filter(Boolean)
    .filter((u, idx, arr) => arr.indexOf(u) === idx)
    .filter(u => !brokenImages.has(u));
  const totalImages = images.length

  const handleImageError = useCallback((url) => {
    setBrokenImages(prev => new Set(prev).add(url));
  }, []);

  // 깨진 이미지로 인해 현재 인덱스가 범위 벗어나면 리셋
  useEffect(() => {
    if (currentImageIndex >= totalImages && totalImages > 0) {
      setCurrentImageIndex(totalImages - 1);
    }
  }, [totalImages, currentImageIndex]);

  // 컴포넌트 마운트 시 게시물 상태 로드
  useEffect(() => {
    if (postData.id) {
      setLikeCount(postData.like_count || 0)
      setCommentCount(postData.comment_count || 0)
      
      // 로그인한 사용자의 좋아요/북마크 상태 확인
      if (isLoggedIn) {
        fetchInteractionStatus()
      }
    }
  }, [postData.id, isLoggedIn])

  // 좋아요/북마크 상태 조회
  const fetchInteractionStatus = async () => {
    try {
      const [likeResponse, bookmarkResponse] = await Promise.all([
        fetch(`/like/status/${postData.id}`, { credentials: 'include' }),
        fetch(`/bookmark/status/${postData.id}`, { credentials: 'include' })
      ])

      const likeData = await likeResponse.json()
      const bookmarkData = await bookmarkResponse.json()

      if (likeData.success) {
        setLiked(likeData.liked)
        setLikeCount(likeData.likeCount)
      }

      if (bookmarkData.success) {
        setBookmarked(bookmarkData.bookmarked)
      }
    } catch (error) {
      console.error('상태 조회 오류:', error)
    }
  }

  // 좋아요 토글
  const handleLikeToggle = async () => {
    if (!isLoggedIn || loading) {
      alert('로그인이 필요합니다.')
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/like/toggle/${postData.id}`, {
        method: 'POST',
        credentials: 'include'
      })

      const data = await response.json()
      if (data.success) {
        setLiked(data.liked)
        setLikeCount(data.likeCount)
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('좋아요 처리 오류:', error)
      alert('좋아요 처리 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  // 북마크 토글
  const handleBookmarkToggle = async () => {
    if (!isLoggedIn || loading) {
      alert('로그인이 필요합니다.')
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/bookmark/toggle/${postData.id}`, {
        method: 'POST',
        credentials: 'include'
      })

      const data = await response.json()
      if (data.success) {
        setBookmarked(data.bookmarked)
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('북마크 처리 오류:', error)
      alert('북마크 처리 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  // 공유 기능
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${postData.author}님의 게시물`,
          text: postData.description?.substring(0, 100) + '...',
          url: `${window.location.origin}/post/${postData.id}`
        })
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard()
        }
      }
    } else {
      copyToClipboard()
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/post/${postData.id}`)
      alert('링크가 클립보드에 복사되었습니다!')
    } catch (error) {
      console.error('클립보드 복사 실패:', error)
    }
  }

  // 게시물 삭제
  const handleDelete = async () => {
    if (!window.confirm('정말로 이 게시물을 삭제하시겠습니까?')) {
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/post/delete/${postData.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      const data = await response.json()
      if (data.success) {
        alert('게시물이 삭제되었습니다.')
        if (onPostDeleted) {
          onPostDeleted(postData.id)
        }
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('게시물 삭제 오류:', error)
      alert('게시물 삭제 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
      setShowDropdown(false)
    }
  }

  const nextImage = () => {
    if (currentImageIndex < totalImages - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
    }
  }

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
    }
  }

  // 현재 사용자가 게시물 작성자인지 확인
  const isAuthor = isLoggedIn && user && (
    user.username === postData.author_username ||
    user.username === postData.author ||
    user.id === postData.author_id
  )

  // 관리자 계정 확인 (username/email/user_account 모두 매칭)
  const ADMIN_ACCOUNTS = ['admin@antsnest.co.kr', 'dexterity', 'admin']
  const isAdmin = isLoggedIn && user && (
    ADMIN_ACCOUNTS.includes(user.username) ||
    ADMIN_ACCOUNTS.includes(user.email) ||
    ADMIN_ACCOUNTS.includes(user.user_account)
  )

  return (
    <Card theme={theme}>
      <CardHeader>
        <UserInfo>
          <Avatar theme={theme}>
            {(postData.author_avatar || postData.authorAvatar) ? (
              <img
                src={postData.author_avatar || postData.authorAvatar}
                alt={`${postData.author_username || postData.author}의 프로필`}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <User size={24} />
            )}
          </Avatar>
          <div>
            <UserName theme={theme} onClick={() => navigate(`/profile/${postData.author_username || postData.author}`)}>
              {postData.author_username || postData.author}
            </UserName>
            <Timestamp theme={theme}>{formatDate(postData.created_at || postData.timestamp)}</Timestamp>
          </div>
        </UserInfo>
        {(isAuthor || isAdmin) && (
          <DropdownContainer>
            <MoreButton theme={theme} onClick={() => setShowDropdown(!showDropdown)}>
              <MoreHorizontal size={20} />
            </MoreButton>
            {showDropdown && (
              <DropdownMenu theme={theme}>
                <DropdownItem theme={theme} onClick={() => {
                  setShowDropdown(false)
                  setShowEditModal(true)
                }}>
                  <Edit size={16} />
                  편집
                </DropdownItem>
                <DropdownItem theme={theme} onClick={handleDelete} className="danger">
                  <Trash2 size={16} />
                  삭제
                </DropdownItem>
              </DropdownMenu>
            )}
          </DropdownContainer>
        )}
      </CardHeader>

      {totalImages > 0 && (
        <>
          <ImageContainer theme={theme}>
            {totalImages > 1 && (
              <div className="image-counter">
                {currentImageIndex + 1} / {totalImages}
              </div>
            )}

            {images[currentImageIndex] && (images[currentImageIndex].toLowerCase().includes('.mp4') || images[currentImageIndex].toLowerCase().includes('.mov') || images[currentImageIndex].toLowerCase().includes('.webm')) ? (
              <div style={{ position: 'relative', width: '100%', minHeight: '200px', background: '#000' }}>
                <div className="video-load-spinner" style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: '36px', height: '36px', border: '3px solid rgba(255,255,255,0.3)',
                  borderTop: '3px solid #fff', borderRadius: '50%',
                  animation: 'videospin 1s linear infinite', zIndex: 1
                }} />
                <style>{`@keyframes videospin { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }`}</style>
                <video
                  src={images[currentImageIndex]}
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  style={{ width: '100%', maxHeight: '500px', objectFit: 'cover', display: 'block', position: 'relative', zIndex: 2 }}
                  onLoadedData={(e) => {
                    const spinner = e.target.parentElement?.querySelector('.video-load-spinner');
                    if (spinner) spinner.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <PostImage
                src={images[currentImageIndex]}
                alt={`게시물 이미지 ${currentImageIndex + 1}`}
                onError={() => handleImageError(images[currentImageIndex])}
                onClick={() => { setLightboxIndex(currentImageIndex); setLightboxOpen(true); }}
              />
            )}

            {totalImages > 1 && (
              <>
                <ImageNavButton className="prev" onClick={prevImage} disabled={currentImageIndex === 0}>
                  <ChevronLeft size={20} />
                </ImageNavButton>

                <ImageNavButton className="next" onClick={nextImage} disabled={currentImageIndex === totalImages - 1}>
                  <ChevronRight size={20} />
                </ImageNavButton>

                <div className="navigation-dots">
                  {images.map((_, index) => (
                    <div
                      key={index}
                      className={`dot ${index === currentImageIndex ? "active" : ""}`}
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </ImageContainer>

          {postData.imageCaption && <ImageCaption theme={theme}>{postData.imageCaption}</ImageCaption>}
        </>
      )}

      <CardActions theme={theme}>
        <ActionButton theme={theme} onClick={handleLikeToggle} $active={liked} disabled={loading}>
          <Heart size={20} />
          <span>좋아요 {likeCount > 0 && `(${likeCount})`}</span>
        </ActionButton>

        <ActionButton theme={theme} onClick={() => setShowComments(!showComments)}>
          <MessageSquare size={20} />
          <span>댓글 {commentCount > 0 && `(${commentCount})`}</span>
        </ActionButton>

        <ActionButton theme={theme} onClick={handleShare}>
          <Share size={20} />
          <span>공유</span>
        </ActionButton>

        <ActionButton theme={theme} onClick={handleBookmarkToggle} $active={bookmarked} disabled={loading}>
          <Bookmark size={20} />
          <span>저장</span>
        </ActionButton>
      </CardActions>

      <CardFooter>
        {postData.tags && postData.tags.length > 0 && (
          <PostTags theme={theme}>
            {postData.tags.map((tag, index) => (
              <span key={index}>#{tag} </span>
            ))}
          </PostTags>
        )}

        {postData.description && (
          <>
            <PostDescription theme={theme} $expanded={descExpanded}>{postData.description}</PostDescription>
            {postData.description.length > 100 && !descExpanded && (
              <ReadMoreButton theme={theme} onClick={() => setDescExpanded(true)}>더 보기</ReadMoreButton>
            )}
          </>
        )}
      </CardFooter>

      {/* 댓글 섹션 */}
      {showComments && (
        <CommentSection 
          postId={postData.id} 
          initialCommentCount={commentCount}
        />
      )}
      
      {/* 편집 모달 */}
      <EditPostModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        post={postData}
        onPostUpdated={() => {
          setShowEditModal(false)
          if (onPostUpdated) {
            onPostUpdated()
          }
        }}
      />

      {/* 이미지 라이트박스 */}
      {lightboxOpen && images.length > 0 && (
        <LightboxOverlay onClick={() => setLightboxOpen(false)}>
          <LightboxClose onClick={() => setLightboxOpen(false)}>
            <X size={24} />
          </LightboxClose>
          {images.length > 1 && (
            <LightboxCounter>{lightboxIndex + 1} / {images.length}</LightboxCounter>
          )}
          <LightboxImage 
            src={images[lightboxIndex]} 
            alt={`이미지 ${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()} 
          />
          {images.length > 1 && lightboxIndex > 0 && (
            <LightboxNav className="prev" onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}>
              <ChevronLeft size={24} />
            </LightboxNav>
          )}
          {images.length > 1 && lightboxIndex < images.length - 1 && (
            <LightboxNav className="next" onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}>
              <ChevronRight size={24} />
            </LightboxNav>
          )}
        </LightboxOverlay>
      )}
    </Card>
  )
}